import React, {useState, useCallback, useRef} from 'react';
import s from './NewHeader.module.css';
import {Link} from 'react-router-dom';
import dropdownArrow from '../../assets/img/Header/downward-arrow 3.png';
import compass from '../../assets/img/Header/compass.png';
import user from '../../assets/img/icons/user.svg';
import cart from '../../assets/img/icons/cart.svg';
import HeaderSearch from './HeaderSearch';
import HeaderSvgSelector from '../../assets/common/SVG/HeaderSvgSelector';
import BurgerMenuNew from './BurgerMenuNew';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {useTranslation} from 'react-i18next';
import {useClickOutside} from '../../hooks/useClickOutside';

interface Props {
	burger: boolean;
	setBurger: (b: boolean) => void;
}

const NewHeader: React.FC<Props> = ({burger, setBurger}) => {
	const apiData = useSelector((state: RootState) => state.appData.appData);
	const apiCurrencies = apiData['currencies'] ?? undefined;
	const [locationSelect, setLocationSelect] = useState({
		open: false,
		data: 'Україна',
	});
	const [currencySelect, setCurrencySelect] = useState(false);
	const [languageSelect, setLanguageSelect] = useState(false);
	const [storeDropdown, setStoreDropdown] = useState(false);
	const [aboutDropdown, setAboutDropdown] = useState(false);
	const [manufacturersDropdown, setManufacturersDropdown] = useState(false);
	
	const storeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const aboutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const manufacturersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	
	const handleStoreMouseEnter = () => {
		if (storeTimeoutRef.current) {
			clearTimeout(storeTimeoutRef.current);
			storeTimeoutRef.current = null;
		}
		setStoreDropdown(true);
	};
	
	const handleStoreMouseLeave = () => {
		storeTimeoutRef.current = setTimeout(() => {
			setStoreDropdown(false);
		}, 200);
	};
	
	const handleAboutMouseEnter = () => {
		if (aboutTimeoutRef.current) {
			clearTimeout(aboutTimeoutRef.current);
			aboutTimeoutRef.current = null;
		}
		setAboutDropdown(true);
	};
	
	const handleAboutMouseLeave = () => {
		aboutTimeoutRef.current = setTimeout(() => {
			setAboutDropdown(false);
		}, 200);
	};
	
	const handleManufacturersMouseEnter = () => {
		if (manufacturersTimeoutRef.current) {
			clearTimeout(manufacturersTimeoutRef.current);
			manufacturersTimeoutRef.current = null;
		}
		setManufacturersDropdown(true);
	};
	
	const handleManufacturersMouseLeave = () => {
		manufacturersTimeoutRef.current = setTimeout(() => {
			setManufacturersDropdown(false);
		}, 200);
	};

	const closeLocation = useCallback(() => {
		setLocationSelect((prev) => {
			if (prev.open) {
				return {...prev, open: false};
			}
			return prev;
		});
	}, []);

	const closeCurrency = useCallback(() => {
		setCurrencySelect((prev) => {
			if (prev) {
				return false;
			}
			return prev;
		});
	}, []);

	const closeLanguage = useCallback(() => {
		setLanguageSelect((prev) => {
			if (prev) {
				return false;
			}
			return prev;
		});
	}, []);

	const closeStore = useCallback(() => {
		setStoreDropdown((prev) => {
			if (prev) {
				return false;
			}
			return prev;
		});
	}, []);

	const closeAbout = useCallback(() => {
		setAboutDropdown((prev) => {
			if (prev) {
				return false;
			}
			return prev;
		});
	}, []);

	const closeManufacturers = useCallback(() => {
		setManufacturersDropdown((prev) => {
			if (prev) {
				return false;
			}
			return prev;
		});
	}, []);

	const locationRef = useClickOutside<HTMLDivElement>(
		locationSelect.open ? closeLocation : () => {},
	);
	const currencyRef = useClickOutside<HTMLDivElement>(
		currencySelect ? closeCurrency : () => {},
	);
	const languageRef = useClickOutside<HTMLDivElement>(
		languageSelect ? closeLanguage : () => {},
	);
	const storeRef = useClickOutside<HTMLDivElement>(
		storeDropdown ? closeStore : () => {},
	);
	const aboutRef = useClickOutside<HTMLDivElement>(
		aboutDropdown ? closeAbout : () => {},
	);
	const manufacturersRef = useClickOutside<HTMLDivElement>(
		manufacturersDropdown ? closeManufacturers : () => {},
	);

	const {t, i18n} = useTranslation();

	// Закрытие всех дропдаунов при открытии нового
	const closeAllDropdowns = () => {
		setLocationSelect((prev) => ({...prev, open: false}));
		setCurrencySelect(false);
		setLanguageSelect(false);
		setStoreDropdown(false);
		setAboutDropdown(false);
		setManufacturersDropdown(false);
	};

	const handleChangeLanguage = (lang: 'uk' | 'en') => {
		setLanguageSelect(false);
		i18n.changeLanguage(lang);
	};

	const handleLocationClick = (locationId: string, locationName: string) => {
		setLocationSelect({
			open: false,
			data: locationName,
		});
		// TODO: Добавить логику изменения локации через API
		console.log('Location changed to:', locationId, locationName);
	};

	const handleCurrencyClick = (currencyCode: string) => {
		setCurrencySelect(false);
		// TODO: Добавить логику изменения валюты через API
		console.log('Currency changed to:', currencyCode);
	};

	const handleKeyDown = (
		e: React.KeyboardEvent,
		action: () => void,
		closeAction?: () => void,
	) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			action();
		} else if (e.key === 'Escape' && closeAction) {
			closeAction();
		}
	};

	return (
		<header className={s.header}>
			<div className={s.headerTopContainer}>
				<div className="container">
					<BurgerMenuNew burger={burger} setBurger={setBurger} />
					<div className="top-header__row">
						<div className="top-header__column">
							<div className={s.headerSearchWrapper}>
								<HeaderSearch
									actionHref={apiData?.catalog}
									textSearch={t('Введіть текст для пошуку...')}
								/>
							</div>
						</div>
						<div className="top-header__column">
							<Link to={apiData?.home} className="top-header__logo">
								<HeaderSvgSelector id={'logo'} />
							</Link>
						</div>
						<div className="top-header__column">
							<div className={s.topHeaderActions}>
								<ul className={s.actionsHeaderList}>
									{apiData.locations && (
										<div
											ref={locationRef}
											role="button"
											tabIndex={0}
											aria-expanded={locationSelect.open}
											aria-haspopup="true"
											onClick={(e) => {
												e.stopPropagation();
												closeAllDropdowns();
												setLocationSelect((prevState) => ({
													open: !prevState.open,
													data: prevState.data,
												}));
											}}
											onKeyDown={(e) =>
												handleKeyDown(
													e,
													() => {
														closeAllDropdowns();
														setLocationSelect((prevState) => ({
															open: !prevState.open,
															data: prevState.data,
														}));
													},
													() =>
														setLocationSelect((prev) => ({
															...prev,
															open: false,
														})),
												)
											}
											className={s.dropdownLocation}
										>
											<img
												style={{
													rotate: locationSelect.open ? '180deg' : '',
													transition: '300ms',
												}}
												src={compass}
												alt=""
											/>
											<div className={s.dropdownSelect}>
												<span>
													{apiData.locations[apiData.location_id]
														? apiData.locations[apiData.location_id]['name']
														: t('Країна')}
												</span>
												<img
													style={{
														rotate: locationSelect.open ? '180deg' : '',
														transition: '300ms',
													}}
													src={dropdownArrow}
													alt=""
												/>
											</div>
											{locationSelect.open && (
												<ul
													className={`${s.dropdown} ${s.dropdownMenuLocation}`}
													role="menu"
												>
													{Object.keys(apiData.locations).map((index: any) => {
														const location_data = apiData.locations[index];
														const isActive = index <= 1;
														return (
															<li
																key={`location_data${location_data?.location_id}`}
																role="menuitem"
																tabIndex={0}
																data-id={location_data?.location_id}
																style={{
																	color: isActive ? '' : '#E8E7E7',
																	cursor: isActive ? 'pointer' : 'not-allowed',
																}}
																onClick={(e) => {
																	e.stopPropagation();
																	if (isActive) {
																		handleLocationClick(
																			location_data?.location_id,
																			location_data?.name,
																		);
																	}
																}}
																onKeyDown={(e) => {
																	if (isActive) {
																		handleKeyDown(e, () =>
																			handleLocationClick(
																				location_data?.location_id,
																				location_data?.name,
																			),
																		);
																	}
																}}
															>
																{location_data?.name}
															</li>
														);
													})}
												</ul>
											)}
										</div>
									)}
									{apiCurrencies && (
										<div
											ref={currencyRef}
											className={s.dropdownCurrencyContainer}
										>
											<div
												role="button"
												tabIndex={0}
												aria-expanded={currencySelect}
												aria-haspopup="true"
												onClick={(e) => {
													e.stopPropagation();
													closeAllDropdowns();
													setCurrencySelect(!currencySelect);
												}}
												onKeyDown={(e) =>
													handleKeyDown(
														e,
														() => {
															closeAllDropdowns();
															setCurrencySelect(!currencySelect);
														},
														() => setCurrencySelect(false),
													)
												}
												className={s.dropdownCurrencyMain}
											>
												<span>
													<div className={s.currencyItem}>
														<span>{apiData.currency_code}</span>|
														<span>
															{apiCurrencies[apiData.currency_code] &&
															apiCurrencies[apiData.currency_code][
																'symbol_right'
															]
																? apiCurrencies[apiData.currency_code][
																		'symbol_right'
																	]
																: apiCurrencies[apiData.currency_code] &&
																	  apiCurrencies[apiData.currency_code][
																			'symbol_left'
																	  ]
																	? apiCurrencies[apiData.currency_code][
																			'symbol_left'
																		]
																	: ''}
														</span>
													</div>
												</span>

												<img
													className={s.currencyDropArrow}
													style={{
														rotate: currencySelect ? '180deg' : '',
														transition: '300ms',
													}}
													src={dropdownArrow}
													alt=""
												/>
											</div>
											{currencySelect && (
												<ul
													className={`${s.dropdown} ${s.dropdownCurrency}`}
													role="menu"
												>
													{Object.keys(apiCurrencies).map((index) => {
														const currency = apiCurrencies[index];
														const isActive =
															currency.code == apiData.currency_code;
														return (
															<li
																key={`currency${currency?.code}`}
																role="menuitem"
																tabIndex={0}
																id={currency.code}
																style={{
																	color: isActive ? '' : '#E8E7E7',
																	cursor: isActive ? 'pointer' : 'not-allowed',
																}}
																onClick={(e) => {
																	e.stopPropagation();
																	if (isActive) {
																		handleCurrencyClick(currency.code);
																	}
																}}
																onKeyDown={(e) => {
																	if (isActive) {
																		handleKeyDown(e, () =>
																			handleCurrencyClick(currency.code),
																		);
																	}
																}}
															>
																{currency.code} |{' '}
																{currency.symbol_right
																	? currency.symbol_right
																	: currency.symbol_left}
															</li>
														);
													})}
												</ul>
											)}
										</div>
									)}
									{apiData.languages && (
										<div ref={languageRef} className={s.dropdownLanguage}>
											<div
												role="button"
												tabIndex={0}
												aria-expanded={languageSelect}
												aria-haspopup="true"
												className={s.dropdownSelect}
												onClick={(e) => {
													e.stopPropagation();
													closeAllDropdowns();
													setLanguageSelect(!languageSelect);
												}}
												onKeyDown={(e) =>
													handleKeyDown(
														e,
														() => {
															closeAllDropdowns();
															setLanguageSelect(!languageSelect);
														},
														() => setLanguageSelect(false),
													)
												}
											>
												<span>
													{apiData.languages[apiData.language_code]
														? apiData.languages[apiData.language_code]['name']
														: apiData.text_language}
												</span>
												<img src={dropdownArrow} alt="" />
											</div>
											{languageSelect && (
												<ul
													className={`${s.dropdown} ${s.dropdownLanguage}`}
													role="menu"
												>
													{Object.keys(apiData.languages).map((index) => {
														const language = apiData.languages[index];
														const isActive =
															language.code == apiData.language_code;
														return (
															<li
																role="menuitem"
																tabIndex={0}
																style={{
																	color: isActive ? '' : '#E8E7E7',
																	cursor: 'pointer',
																}}
																onClick={(e) => {
																	e.stopPropagation();
																	handleChangeLanguage(
																		language.code.slice(0, 2) as 'uk' | 'en',
																	);
																}}
																onKeyDown={(e) =>
																	handleKeyDown(e, () =>
																		handleChangeLanguage(
																			language.code.slice(0, 2) as 'uk' | 'en',
																		),
																	)
																}
																key={`languageCode${language.code}`}
																id={language.code}
															>
																{language.name}
															</li>
														);
													})}
												</ul>
											)}
										</div>
									)}
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className={s.headerBottomContainer}>
				<div className="container">
					<div className="menu">
						<nav className="menu__body">
							<ul className={`${s.menuList}`}>
								<div
									ref={storeRef}
									className={s.dropdownBottomMenu}
									onMouseEnter={handleStoreMouseEnter}
									onMouseLeave={handleStoreMouseLeave}
								>
									<button
										className={s.dropdownButton}
										onClick={() => setStoreDropdown(!storeDropdown)}
										aria-expanded={storeDropdown}
										aria-haspopup="true"
									>
										{t('Магазин')}
									</button>
									<div
										className={`${s.dropdownContent} ${
											storeDropdown ? s.dropdownContentOpen : ''
										}`}
										onMouseEnter={handleStoreMouseEnter}
										onMouseLeave={handleStoreMouseLeave}
									>
										<Link to={apiData.catalog}>{t('Каталог')}</Link>
										{apiData.categories &&
											apiData.categories.length > 0 &&
											apiData.categories.map((category: any) => {
												if (category.top) {
													return (
														<Link
															key={`bottomHeader${category.category_id}`}
															to={category.href}
														>
															{category.name}
														</Link>
													);
												}
											})}
									</div>
								</div>
								<div
									ref={aboutRef}
									className={s.dropdownBottomMenu}
									onMouseEnter={handleAboutMouseEnter}
									onMouseLeave={handleAboutMouseLeave}
								>
									<button
										className={s.dropdownButton}
										onClick={() => setAboutDropdown(!aboutDropdown)}
										aria-expanded={aboutDropdown}
										aria-haspopup="true"
									>
										{t('Про компанію')}
									</button>
									<div
										className={`${s.dropdownContent} ${
											aboutDropdown ? s.dropdownContentOpen : ''
										}`}
										onMouseEnter={handleAboutMouseEnter}
										onMouseLeave={handleAboutMouseLeave}
									>
										{apiData.top_informations.map((information: any) => {
											if (parseInt(information.sort_order) < 0) {
												return (
													<Link
														key={`bottomHeader${information.information_id}`}
														to={information.href}
													>
														{information.title}
													</Link>
												);
											}
										})}
									</div>
								</div>
								{apiData.top_informations.map((information: any) => {
									if (parseInt(information.sort_order) == 0) {
										return (
											<li
												key={information.information_id}
												className="menu__item"
											>
												<a href={information.href} className="menu__link">
													{information.title}
												</a>
											</li>
										);
									}
								})}
								<div
									ref={manufacturersRef}
									className={s.dropdownBottomMenu}
									onMouseEnter={handleManufacturersMouseEnter}
									onMouseLeave={handleManufacturersMouseLeave}
								>
									<button
										className={s.dropdownButton}
										onClick={() =>
											setManufacturersDropdown(!manufacturersDropdown)
										}
										aria-expanded={manufacturersDropdown}
										aria-haspopup="true"
									>
										{t('Виробники')}
									</button>
									<div
										className={`${s.dropdownContent} ${
											manufacturersDropdown ? s.dropdownContentOpen : ''
										}`}
										onMouseEnter={handleManufacturersMouseEnter}
										onMouseLeave={handleManufacturersMouseLeave}
									>
										{apiData.manufacturers.map((manufacturer: any) => {
											return (
												<a
													key={`manufacturer${manufacturer.manufacturer_id}`}
													href={manufacturer.href}
												>
													{manufacturer.name}
												</a>
											);
										})}
									</div>
								</div>
								{apiData.top_informations.map((information: any) => {
									if (parseInt(information.sort_order) > 0) {
										return (
											<li
												key={`information_id${information?.information_id}`}
												className="menu__item"
											>
												<a href={information.href} className="menu__link">
													{information.title}
												</a>
											</li>
										);
									}
								})}
								<li className="menu__item">
									<a href={apiData.contact} className="menu__link">
										{t('Контакти')}
									</a>
								</li>
								{apiData.top_blog.map((top_blog_cat: any, index: number) => {
									if (!index) {
										return (
											<li
												key={`top_blog_cat${top_blog_cat.category_id}`}
												className="menu__item"
											>
												<a href={top_blog_cat.href} className="menu__link">
													{top_blog_cat.name}
												</a>
											</li>
										);
									}
								})}
								<div className={s.userCartIcons}>
									<li className={s.userCartIcons_item}>
										<a
											href={apiData.account_link}
											className="actions-top-header__user popup-link"
										>
											<img src={user} alt="user" />
										</a>
									</li>
									<li className={s.userCartIcons_item}>
										<Link
											to={'/cart'}
											/*to={apiData.cart_link}*/
											className="actions-top-header__cart"
										>
											<img src={cart} alt="cart" />
											<span>{apiData.cart_total}</span>
										</Link>
									</li>
								</div>
							</ul>
						</nav>
					</div>
				</div>
			</div>
		</header>
	);
};

export default NewHeader;
